import React from 'react';
import { DynamicField } from './DynamicField';
import type { DynamicFormProps, DynamicFormData } from './types';

export const DynamicForm: React.FC<DynamicFormProps> = ({
  schema,
  data,
  onChange,
  disabled = false,
}) => {
  const handleFieldChange = (fieldName: string, value: any) => {
    const newData: DynamicFormData = {
      ...data,
      [fieldName]: value,
    };
    onChange(newData);
  };

  const isFieldRequired = (fieldName: string): boolean => {
    return schema.required?.includes(fieldName) || false;
  };

  // Validation function (can be used for real-time validation in the future)
  const validateForm = (): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    // Check required fields
    if (schema.required) {
      for (const requiredField of schema.required) {
        const value = data[requiredField];
        if (value === undefined || value === null || value === '') {
          const fieldSchema = schema.properties[requiredField];
          const fieldLabel = fieldSchema?.description || requiredField;
          errors.push(`${fieldLabel}은(는) 필수 입력 항목입니다.`);
        }
      }
    }

    // Validate field constraints
    Object.entries(schema.properties).forEach(([fieldName, fieldSchema]) => {
      const value = data[fieldName];
      if (value !== undefined && value !== null && value !== '') {
        
        // String validations
        if (fieldSchema.type === 'string' && typeof value === 'string') {
          if (fieldSchema.minLength && value.length < fieldSchema.minLength) {
            errors.push(`${fieldSchema.description || fieldName}은(는) 최소 ${fieldSchema.minLength}자 이상이어야 합니다.`);
          }
          if (fieldSchema.maxLength && value.length > fieldSchema.maxLength) {
            errors.push(`${fieldSchema.description || fieldName}은(는) 최대 ${fieldSchema.maxLength}자 이하여야 합니다.`);
          }
          if (fieldSchema.pattern && !new RegExp(fieldSchema.pattern).test(value)) {
            errors.push(`${fieldSchema.description || fieldName}의 형식이 올바르지 않습니다.`);
          }
        }

        // Number validations
        if ((fieldSchema.type === 'number' || fieldSchema.type === 'integer') && typeof value === 'number') {
          if (fieldSchema.minimum !== undefined && value < fieldSchema.minimum) {
            errors.push(`${fieldSchema.description || fieldName}은(는) ${fieldSchema.minimum} 이상이어야 합니다.`);
          }
          if (fieldSchema.maximum !== undefined && value > fieldSchema.maximum) {
            errors.push(`${fieldSchema.description || fieldName}은(는) ${fieldSchema.maximum} 이하여야 합니다.`);
          }
        }
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
    };
  };

  // Sort fields to show required fields first
  const sortedFieldNames = Object.keys(schema.properties).sort((a, b) => {
    const aRequired = isFieldRequired(a);
    const bRequired = isFieldRequired(b);
    
    if (aRequired && !bRequired) return -1;
    if (!aRequired && bRequired) return 1;
    return a.localeCompare(b);
  });

  if (!schema.properties || Object.keys(schema.properties).length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        이 작업에는 추가 설정이 필요하지 않습니다.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {sortedFieldNames.map((fieldName) => {
          const fieldSchema = schema.properties[fieldName];
          return (
            <DynamicField
              key={fieldName}
              name={fieldName}
              schema={fieldSchema}
              value={data[fieldName]}
              onChange={(value) => handleFieldChange(fieldName, value)}
              required={isFieldRequired(fieldName)}
              disabled={disabled}
            />
          );
        })}
      </div>
      
      {/* Display validation info */}
      {schema.required && schema.required.length > 0 && (
        <div className="text-xs text-gray-500 mt-2">
          <span className="text-red-500">*</span> 필수 입력 항목
        </div>
      )}
    </div>
  );
};

// Export validation function for external use
export const validateDynamicForm = (schema: DynamicFormProps['schema'], data: DynamicFormData) => {
  const errors: string[] = [];
  
  // Check required fields
  if (schema.required) {
    for (const requiredField of schema.required) {
      const value = data[requiredField];
      if (value === undefined || value === null || value === '') {
        const fieldSchema = schema.properties[requiredField];
        const fieldLabel = fieldSchema?.description || requiredField;
        errors.push(`${fieldLabel}은(는) 필수 입력 항목입니다.`);
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};